import {Injectable, NgZone} from '@angular/core';
import {NotifyService} from '../notify/notify.service';

/* ══════════════════════════════════════════════════════════════════════
   Right-click → copy a client's name, email or phone (console-v2 design,
   Oct 2026).

   ONE document-level listener, installed by AppComponent - nothing per row.
   The menu offers, in this order:
     1. whatever the nearest [gcCopy] element says (name / email / phone of
        the contact, site contact or account it marks), and
     2. any email or phone number in the exact text you right-clicked
        (the "0400 000 011 · x@y.com" sub-lines, mailto:/tel: links)
   - de-duplicated, plus "Copy all" when there is more than one value.

   It stays out of the way: no values → the browser's own menu; a text
   selection under the cursor → the browser's menu (you meant to copy THAT);
   inputs and editors → the browser's menu; an outside link (a website) or an
   image → the browser's menu; a component that handled the event itself
   (defaultPrevented) → untouched; Shift held → the browser's menu.
   While it is open it owns Escape and the arrow keys (nothing behind it - a
   Pebbl prompt, the email composer - reacts to them), and Escape or a copy
   hands the keyboard focus back to wherever it was.

   Touch: iOS never fires `contextmenu`, so a 550ms long-press on a [gcCopy]
   element opens the same menu. Android fires both; the second is swallowed,
   and so is the one click the lifting finger may produce - never a later tap.

   Plain DOM on purpose: it lives on <body> so no table card, sheet or
   `overflow: clip` ancestor can crop it, and it runs outside Angular so the
   scroll / pointer listeners cost no change detection.
   ══════════════════════════════════════════════════════════════════════ */

interface CopyItem {
  kind: 'name' | 'email' | 'phone';
  value: string;
}

const EMAIL_RX = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;
const FILE_NAME_RX = /\.(?:png|jpe?g|gif|svg|webp|heic|pdf|docx?|xlsx?|csv|zip)$/i;
/* Australian numbers only, ten digits: 04xx xxx xxx mobiles, 02/03/07/08
   landlines (also written (02) xxxx xxxx), +61 …, and 1300 / 1800. v2's looser
   pattern also took the tail of longer numbers as a phone - "004 085 616" out
   of an ABN, "001670098" out of a BPAY reference - so those now need to stand
   on their own (see phonesIn). */
const PHONE_RX = /(?:\+61[\s-]?[2-478](?:[\s-]?\d){8}|\(0[2378]\)\s?\d{4}[\s-]?\d{4}|0[2-478](?:[\s-]?\d){8}|1[38]00(?:[\s-]?\d){6})(?![\s-]?\d)/g;
const MARK = '[data-copy-name],[data-copy-email],[data-copy-phone]';

const LABEL: Record<CopyItem['kind'], string> = {name: 'Name', email: 'Email', phone: 'Phone'};
const ICON: Record<CopyItem['kind'] | 'all', string> = {
  name: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  email: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/>',
  phone: '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.1 9.9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>',
  all: '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>'
};

@Injectable({providedIn: 'root'})
export class CopyMenuService {
  private menu: HTMLElement | null = null;
  private installed = false;
  private pressTimer: ReturnType<typeof setTimeout> | null = null;
  private pressAt = {x: 0, y: 0};
  private openedByPressTs = 0;
  /** where the keyboard focus was before the menu took it */
  private returnFocus: HTMLElement | null = null;

  constructor(private notify: NotifyService, private zone: NgZone) {
  }

  install(): void {
    if (this.installed || typeof document === 'undefined') { return; }
    this.installed = true;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('contextmenu', e => this.onContext(e));
      document.addEventListener('pointerdown', e => this.onPointerDown(e), true);
      document.addEventListener('pointermove', e => this.onPointerMove(e), true);
      document.addEventListener('pointerup', () => this.cancelPress(), true);
      document.addEventListener('pointercancel', () => this.cancelPress(), true);
      document.addEventListener('keydown', e => this.onKey(e), true);
      document.addEventListener('scroll', () => this.close(), true);
      window.addEventListener('resize', () => this.close());
      window.addEventListener('blur', () => this.close());
      document.addEventListener('gc-close-popovers', () => this.close());
    });
  }

  /* ── triggers ─────────────────────────────────────────────────────── */

  private onContext(e: MouseEvent): void {
    if (Date.now() - this.openedByPressTs < 1000) { e.preventDefault(); return; }
    // Android's own long-press contextmenu got here first: the press timer
    // must not open the menu a second time on top of it
    this.cancelPress();
    if (e.defaultPrevented || e.shiftKey) { return; }   // Shift = always the native menu
    const target = e.target as Element | null;
    if (!target || this.skip(target)) { return; }
    const items = this.collect(target);
    if (!items.length) { return; }
    e.preventDefault();
    this.open(items, e.clientX, e.clientY);
  }

  private onPointerDown(e: PointerEvent): void {
    if (this.menu && !this.menu.contains(e.target as Node)) { this.close(); }
    if (e.pointerType !== 'touch') { return; }
    const target = e.target as Element | null;
    if (!target?.closest(MARK) || this.skip(target)) { return; }
    this.cancelPress();
    this.pressAt = {x: e.clientX, y: e.clientY};
    this.pressTimer = setTimeout(() => {
      this.pressTimer = null;
      const items = this.collect(target);
      if (!items.length) { return; }
      this.openedByPressTs = Date.now();
      this.open(items, this.pressAt.x, this.pressAt.y);
      // The finger lifting must not also follow the link it was resting on. Only
      // THAT click: a long-press often ends with no click at all, so the trap is
      // dropped shortly after the finger lifts rather than left to eat the next
      // tap (a tap on the menu itself is never eaten).
      const swallow = (ev: MouseEvent) => {
        if (this.menu?.contains(ev.target as Node)) { return; }
        ev.preventDefault();
        ev.stopPropagation();
      };
      const disarm = () => setTimeout(() => document.removeEventListener('click', swallow, true), 400);
      document.addEventListener('click', swallow, {capture: true, once: true});
      document.addEventListener('pointerup', disarm, {capture: true, once: true});
      document.addEventListener('pointercancel', disarm, {capture: true, once: true});
    }, 550);
  }

  private onPointerMove(e: PointerEvent): void {
    if (this.pressTimer && Math.hypot(e.clientX - this.pressAt.x, e.clientY - this.pressAt.y) > 10) {
      this.cancelPress();
    }
  }

  private cancelPress(): void {
    if (this.pressTimer) { clearTimeout(this.pressTimer); this.pressTimer = null; }
  }

  private skip(el: Element): boolean {
    if (el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], .gc-copy-menu')) {
      return true;
    }
    // an outside link (an account's website) or a picture keeps the browser's
    // "open in new tab" / "save image" menu
    if (el.closest('a[href^="http"], a[target="_blank"], img')) {
      return true;
    }
    // a live selection under the cursor means "copy what I selected"
    const sel = window.getSelection();
    return !!sel && !sel.isCollapsed && sel.toString().trim().length > 0 && sel.containsNode(el, true);
  }

  /* ── what can be copied here ──────────────────────────────────────── */

  private collect(target: Element): CopyItem[] {
    const out: CopyItem[] = [];
    const add = (kind: CopyItem['kind'], value: string | null | undefined) => {
      const v = (value || '').trim();
      if (v && !out.some(i => i.kind === kind && norm(i.value) === norm(v))) { out.push({kind, value: v}); }
    };

    const marked = target.closest(MARK);
    if (marked) {
      add('name', marked.getAttribute('data-copy-name'));
      add('email', marked.getAttribute('data-copy-email'));
      add('phone', marked.getAttribute('data-copy-phone'));
    }

    const link = target.closest('a[href^="mailto:"], a[href^="tel:"]') as HTMLAnchorElement | null;
    if (link) {
      const href = decodeURIComponent(link.getAttribute('href') || '');
      add(href.startsWith('mailto:') ? 'email' : 'phone', href.replace(/^(mailto|tel):/, '').split('?')[0]);
    }

    // only the text actually under the cursor - never a whole card's worth
    const text = (target.textContent || '').trim();
    if (text && text.length <= 160) {
      for (const m of text.match(EMAIL_RX) || []) {
        if (!FILE_NAME_RX.test(m)) { add('email', m); }   // "logo@2x.png" is a file, not an address
      }
      for (const m of phonesIn(text)) { add('phone', m); }
    }
    return out;
  }

  /* ── the menu ─────────────────────────────────────────────────────── */

  private open(items: CopyItem[], x: number, y: number): void {
    document.dispatchEvent(new Event('gc-close-popovers'));   // one popover app-wide
    this.close();
    const had = document.activeElement;
    this.returnFocus = had instanceof HTMLElement && had !== document.body ? had : null;

    const menu = document.createElement('div');
    menu.className = 'gc-copy-menu';
    menu.setAttribute('role', 'menu');
    menu.tabIndex = -1;
    menu.innerHTML = '<div class="gc-copy-cap">Copy to clipboard</div>';

    const row = (icon: string, k: string, v: string, run: () => void) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gc-copy-opt';
      b.setAttribute('role', 'menuitem');
      b.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${icon}</svg>
        <span class="gc-copy-k"></span><span class="gc-copy-v"></span>`;
      (b.querySelector('.gc-copy-k') as HTMLElement).textContent = k;
      (b.querySelector('.gc-copy-v') as HTMLElement).textContent = v;
      b.addEventListener('click', ev => { ev.stopPropagation(); run(); this.close(true); });
      menu.appendChild(b);
    };

    for (const it of items) {
      row(ICON[it.kind], LABEL[it.kind], it.value, () => this.copy(it.value, `${LABEL[it.kind]} copied`));
    }
    if (items.length > 1) {
      const sep = document.createElement('div');
      sep.className = 'gc-copy-sep';
      menu.appendChild(sep);
      const all = items.map(i => i.value).join('\n');
      row(ICON.all, 'Copy all', `${items.length} details`, () => this.copy(all, 'Contact details copied'));
    }

    document.body.appendChild(menu);
    // keep it on screen: flip left / up when the cursor is near an edge
    const r = menu.getBoundingClientRect();
    const left = x + r.width > window.innerWidth - 8 ? Math.max(8, x - r.width) : x;
    const top = y + r.height > window.innerHeight - 8 ? Math.max(8, y - r.height) : y;
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    this.menu = menu;
    // the hover tooltip of what was right-clicked must not sit over the menu
    document.documentElement.classList.add('gc-copy-open');
    // Focus the panel, not its first row: a focused row draws the hover fill,
    // so the menu opened with Name already looking picked. The arrow keys
    // still step into the rows from here (see onKey).
    menu.focus({preventScroll: true});
  }

  /** `refocus`: after Escape or a copy, put the keyboard back where it was (the
   *  search box, a dialog's field) - the menu took it, so nothing else will. A
   *  click elsewhere, a scroll or a resize leave focus to that action. */
  private close(refocus = false): void {
    if (!this.menu) { return; }
    this.menu.remove();
    this.menu = null;
    document.documentElement.classList.remove('gc-copy-open');
    const back = this.returnFocus;
    this.returnFocus = null;
    if (refocus && back?.isConnected) { back.focus({preventScroll: true}); }
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.menu) { return; }
    if (e.key !== 'Escape' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') { return; }
    // The menu owns these keys while it is open: without this, a page's own
    // Escape handler (Pebbl prompt bar, full-page composer, expanded chart)
    // fired as well and threw away what was open behind the menu. This runs in
    // the capture phase on document, so nothing below it sees the key.
    e.preventDefault();
    e.stopImmediatePropagation();
    if (e.key === 'Escape') { this.close(true); return; }
    const opts = Array.from(this.menu.querySelectorAll<HTMLElement>('.gc-copy-opt'));
    const i = opts.indexOf(document.activeElement as HTMLElement);
    // nothing focused yet (just opened): Down starts at the first row, Up at the last
    const next = e.key === 'ArrowDown'
      ? (i + 1) % opts.length
      : (i < 0 ? opts.length - 1 : (i - 1 + opts.length) % opts.length);
    opts[next]?.focus();
  }

  private copy(value: string, msg: string): void {
    // the listeners run outside Angular; the toast host must see the change
    const done = () => this.zone.run(() => this.notify.success(msg, {detail: value.replace(/\n/g, ' · ')}));
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(done, () => this.fallback(value) && done());
    } else if (this.fallback(value)) {
      done();
    }
  }

  /** http:// dev hosts on a LAN IP have no async clipboard */
  private fallback(value: string): boolean {
    // select() moves the focus into the helper box; give it back afterwards
    const had = document.activeElement;
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    ta.remove();
    if (had instanceof HTMLElement && had !== document.body && had.isConnected) { had.focus({preventScroll: true}); }
    if (!ok) { this.zone.run(() => this.notify.error('Could not copy - your browser blocked the clipboard')); }
    return ok;
  }
}

const norm = (s: string) => s.replace(/[\s()-]/g, '').toLowerCase();

/** The phone numbers in a piece of text, skipping any run that is really the
 *  end of a longer number or code: one right after a letter, digit or "+", or
 *  after a digit and a single space / hyphen ("ABN 53 004 085 616"). Checked here rather than
 *  with a regex lookbehind so older Safari can still parse the bundle. */
function phonesIn(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(PHONE_RX)) {
    const i = m.index ?? 0;
    const p1 = text[i - 1] ?? '';
    const p2 = text[i - 2] ?? '';
    // stuck to a code ("INV0412345678") or the tail of a longer number
    if (/[\w+]/.test(p1) || (/[\s-]/.test(p1) && /\d/.test(p2))) { continue; }
    out.push(m[0]);
  }
  return out;
}
